import React from 'react';
import {
  TextField,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Typography,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pl';
import AccordionCard from '../../../Shared/Components/AccordionCard/AccordionCard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { CashDocumentType } from '../../Types/cashDocumentTypes';
import type { Control, FieldErrors } from 'react-hook-form';
import type { CreateCashDocumentFormData } from '../../Validation/cashDocument.schema';
import { useGetCashReportCompaniesLookupQuery } from '../../Service/cashReportCompaniesLookupApi';

export interface CashDocumentFormSectionProps {
  control: Control<CreateCashDocumentFormData>;
  errors: FieldErrors<CreateCashDocumentFormData>;
  watchType?: CashDocumentType;
  watchInvoiceId?: number | null;
  showCompanySelect?: boolean;
  companyId?: number | null;
  onCompanyChange?: (id: number | null) => void;
  companyError?: string;
}

const CashDocumentFormSection: React.FC<CashDocumentFormSectionProps> = ({
  control,
  errors,
  watchType,
  watchInvoiceId,
  showCompanySelect = false,
  companyId,
  onCompanyChange,
  companyError,
}) => {
  const { companies, isLoading: isCompaniesLoading } = useGetCashReportCompaniesLookupQuery({
    skip: !showCompanySelect,
  });

  return (
    <AccordionCard title="Dokument kasowy" icon={<ReceiptIcon color="primary" />}>
      <Grid container spacing={2}>
        {/* Company selector (manual create flow only, no reservation/invoice context) */}
        {showCompanySelect && (
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth error={!!companyError}>
              <InputLabel id="company-select-label">Firma *</InputLabel>
              <Select
                labelId="company-select-label"
                value={companyId !== null && companyId !== undefined ? String(companyId) : ''}
                label="Firma *"
                disabled={isCompaniesLoading}
                onChange={(e) => {
                  onCompanyChange?.(e.target.value !== '' ? Number(e.target.value) : null);
                }}
              >
                <MenuItem value=""><em>Wybierz firmę...</em></MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={String(company.id)}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
              {companyError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                  {companyError}
                </Typography>
              )}
            </FormControl>
          </Grid>
        )}

        {/* Document Type (KP/KW) */}
        <Grid size={{ xs: 12, md: 6 }} >
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.type}>
                <InputLabel>Typ dokumentu *</InputLabel>
                <Select {...field} label="Typ dokumentu *">
                  <MenuItem value={CashDocumentType.KP}>KP - Kasa przyjęła</MenuItem>
                  <MenuItem value={CashDocumentType.KW}>KW - Kasa wydała</MenuItem>
                </Select>
                {errors.type && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.type.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </Grid>

        {/* Document Date */}
        <Grid size={{ xs: 12, md: 6 }} >
          <Controller
            name="documentDate"
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
                <DatePicker
                  label="Data dokumentu *"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(newVal: Dayjs | null) => {
                    field.onChange(newVal ? newVal.format('YYYY-MM-DD') : '');
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.documentDate,
                      helperText: errors.documentDate?.message,
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </Grid>

        {/* Amount */}
        <Grid size={{ xs: 12, md: 6 }} >
          <Controller
            name="amount"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type="number"
                label="Kwota *"
                fullWidth
                error={!!errors.amount}
                helperText={errors.amount?.message}
                inputProps={{
                  step: '0.01',
                  min: '0',
                }}
              />
            )}
          />
        </Grid>

        {/* Currency */}
        <Grid size={{ xs: 12, md: 6 }} >
          <Controller
            name="currency"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.currency}>
                <InputLabel>Waluta *</InputLabel>
                <Select {...field} label="Waluta *">
                  <MenuItem value="PLN">PLN - Polski złoty</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                  <MenuItem value="USD">USD - Dolar amerykański</MenuItem>
                  <MenuItem value="GBP">GBP - Funt brytyjski</MenuItem>
                </Select>
                {errors.currency && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.currency.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </Grid>

        {/* Person Name */}
        <Grid size={{ xs: 12 }} >
          <Controller
            name="personName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={
                  watchType === CashDocumentType.KP
                    ? 'Przyjęto od (imię i nazwisko) *'
                    : watchType === CashDocumentType.KW
                    ? 'Wydano dla (imię i nazwisko) *'
                    : 'Imię i nazwisko *'
                }
                fullWidth
                error={!!errors.personName}
                helperText={errors.personName?.message}
              />
            )}
          />
        </Grid>

        {/* Purpose */}
        <Grid size={{ xs: 12 }} >
          <Controller
            name="purpose"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Cel płatności *"
                fullWidth
                multiline
                rows={2}
                error={!!errors.purpose}
                helperText={errors.purpose?.message}
                placeholder="np. Wpłata zaliczki za wyjazd do Grecji"
              />
            )}
          />
        </Grid>

        {/* Note */}
        <Grid size={{ xs: 12 }} >
          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Notatka (opcjonalnie)"
                fullWidth
                multiline
                rows={3}
                error={!!errors.note}
                helperText={errors.note?.message}
              />
            )}
          />
        </Grid>

        {/* Issued By */}
        <Grid size={{ xs: 12, md: 6 }} >
          <Controller
            name="issuedBy"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Wystawił (opcjonalnie)"
                fullWidth
                error={!!errors.issuedBy}
                helperText={errors.issuedBy?.message}
              />
            )}
          />
        </Grid>

        {/* Invoice Auto-Payment Warning */}
        {watchInvoiceId && (
          <Grid size={{ xs: 12 }} >
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Automatyczna płatność:</strong> Dokument jest powiązany z fakturą.
                Transakcja zostanie automatycznie zarejestrowana w systemie płatności.
              </Typography>
            </Alert>
          </Grid>
        )}
      </Grid>
    </AccordionCard>
  );
};

export default CashDocumentFormSection;
